import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

export const bottomNavbarStyles = makeStyles<{ activeIndex: number }>()(
  (theme: Theme, {}) => ({
    // ─── Root container ──────────────────────────────────────────────────────
    root: {
      width: '100%',
      position: 'fixed',
      bottom: 0,
      left: 0,
      zIndex: 1200,
      backgroundColor: '#ffffff',
      borderTop: '1px solid #E2DFE8',
      boxShadow: '0px -4px 16px rgba(0,0,0,0.05)',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      paddingLeft: 'env(safe-area-inset-left, 0px)',
      paddingRight: 'env(safe-area-inset-right, 0px)',
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },

    // ─── Inner nav row ───────────────────────────────────────────────────────
    navRow: {
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'stretch',
      // Responsive inner width: grows up to 420px on wider phones
      maxWidth: 420,
      width: '100%',
      margin: '0 auto',
      paddingTop: 6,
      paddingBottom: 6,
      // On narrower phones give items a little more vertical breathing room
      '@media (max-height: 700px)': {
        paddingTop: 4,
        paddingBottom: 4,
      },
    },

    // ─── Individual nav item ─────────────────────────────────────────────────
    navItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      gap: 3,
      cursor: 'pointer',
      // Equal flex share; each item is at least 56 px wide for touch comfort
      flex: '1 1 0',
      minWidth: 56,
      padding: '6px 4px',
      borderRadius: 12,
      // Remove default link / text decoration artefacts
      textDecoration: 'none',
      color: 'rgba(0, 0, 0, 0.38)',
      // Smooth colour and scale transitions for all states
      transition:
        'color 0.22s ease, background-color 0.22s ease, transform 0.18s ease',
      // Pressing feedback
      '&:active': {
        transform: 'scale(0.92)',
      },
      // Hover tint for devices with a pointer (desktop / tablet)
      '@media (hover: hover)': {
        '&:hover': {
          backgroundColor: `${theme.palette.primary.main}08`,
        },
      },
    },

    // Active state: primary color, no background tint
    navItemActive: {
      color: theme.palette.primary.main,
    },

    // Inactive state: muted grey
    navItemInactive: {
      color: 'rgba(0,0,0,0.38)',
    },

    // ─── Icon wrapper (holds SVG + active-dot) ───────────────────────────────
    iconWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 28,
      height: 28,
      // Scale up the active icon slightly for emphasis
      '& svg': {
        transition: 'transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1)',
      },
    },

    iconWrapperActive: {
      '& svg': {
        transform: 'scale(1.12)',
      },
    },

    // ─── Active indicator dot below the icon ─────────────────────────────────
    activeDot: {
      position: 'absolute',
      bottom: -6,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 4,
      height: 4,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      transition: 'opacity 0.22s ease, transform 0.22s ease',
    },

    activeDotVisible: {
      opacity: 1,
      transform: 'translateX(-50%) scale(1)',
    },

    activeDotHidden: {
      opacity: 0,
      transform: 'translateX(-50%) scale(0)',
    },

    // ─── Label ───────────────────────────────────────────────────────────────
    label: {
      fontFamily: "'Inter', 'Gotham Book', sans-serif",
      fontSize: 10,
      fontWeight: 400,
      lineHeight: 1.2,
      letterSpacing: '0.02em',
      textAlign: 'center',
      transition: 'font-weight 0.18s ease, color 0.18s ease',
      // Prevent long translations from wrapping awkwardly
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 80,
    },

    labelActive: {
      fontWeight: 700,
    },

    // ─── Badge (pending count) ───────────────────────────────────────────────
    badge: {
      position: 'absolute',
      // Sits above and to the right of the icon
      top: 0,
      right: 30,
      minWidth: 18,
      height: 18,
      paddingLeft: 4,
      paddingRight: 4,
      borderRadius: 9,
      backgroundColor: theme.palette.warning.main,
      color: '#fff',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      // Thin white ring so the badge pops against any icon colour
      boxShadow: '0 0 0 2px #ffffff',
      // Entrance animation
      animation: 'badgePop 0.28s cubic-bezier(0.34, 1.56, 0.64, 1) both',
      '@keyframes badgePop': {
        '0%': { transform: 'scale(0)' },
        '100%': { transform: 'scale(1)' },
      },
    },

    badgeLabel: {
      fontFamily: "'Inter', 'Gotham Book', sans-serif",
      fontSize: 9,
      fontWeight: 700,
      lineHeight: 1,
      letterSpacing: '0.01em',
    },
  }),
);
